import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./LodgeDetailsModal.css";
import Select from "react-select";
import { applyImageFallback, documentThumbnailUrl } from "../utils/documentPreviews";
import MemberDetailsModal from "./MemberDetailsModal";

function LodgeDetailsModal({ onClose, onSave, initialLodgeId }) {
    const [lodges, setLodges] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [lodgeTypes, setLodgeTypes] = useState([]);
    const [constitutions, setConstitutions] = useState([]);
    const [memberOptions, setMemberOptions] = useState([]);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [membershipStatuses, setMembershipStatuses] = useState([]);

    const [selectedLodge, setSelectedLodge] = useState(null);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});

    const [locationHistory, setLocationHistory] = useState([]);
    const [locationForm, setLocationForm] = useState({});
    const [editingLocationId, setEditingLocationId] = useState(null);
    const [activeTab, setActiveTab] = useState("overview");
    const [memberLinkForm, setMemberLinkForm] = useState({
        member_id: "",
        membership_type_id: "",
        membership_status_id: "",
        start_date: "",
    });
    const [duesYear, setDuesYear] = useState(new Date().getFullYear());
    const [duesRateForm, setDuesRateForm] = useState({
        membership_type_id: "",
        amount_due: "",
        due_date: "",
        notes: "",
    });
    const emptyDuesSettings = {
        period_label: "",
        bank_name: "",
        account_name: "",
        account_number: "",
        branch_code: "",
        default_due_date: "",
        reference_instructions: "",
        payment_notes: "",
    };
    const [duesSettingsForm, setDuesSettingsForm] = useState(emptyDuesSettings);
    const [duesMemberFilter, setDuesMemberFilter] = useState("current");
    const [lodgeDues, setLodgeDues] = useState({ rates: [], members: [], availableYears: [] });
    const [editingMemberId, setEditingMemberId] = useState(null);
    const [lodgeContext, setLodgeContext] = useState({
        documents: [],
        meetings: [],
        members: [],
        counts: { documents: 0, meetings: 0, members: 0 },
    });

    // Fetch data on component mount
    useEffect(() => {
        fetchLodges();
        fetchBuildings();
        fetchLodgeTypes();
        fetchConstitutions();
        fetchMemberLinkOptions();
    }, []);

    useEffect(() => {
        if (!initialLodgeId || !lodges.length || selectedLodge?.id === initialLodgeId) return;
        handleLodgeSelect(initialLodgeId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLodgeId, lodges, selectedLodge?.id]);

    const fetchLodges = async () => {
        try {
            const res = await axios.get("/api/resources/lodges");
            setLodges(res.data);
        } catch (error) {
            console.error("Error fetching lodges:", error);
        }
    };

    const fetchBuildings = async () => {
        try {
            const res = await axios.get("/api/buildings");
            setBuildings(res.data);
        } catch (error) {
            console.error("Error fetching buildings:", error);
        }
    };

    const fetchLodgeTypes = async () => {
        try {
            const res = await axios.get("/api/resources/lodge-types");
            setLodgeTypes(res.data);
        } catch (error) {
            console.error("Error fetching lodge types:", error);
        }
    };

    const fetchConstitutions = async () => {
        try {
            const res = await axios.get("/api/resources/constitutions");
            setConstitutions(res.data);
        } catch (error) {
            console.error("Error fetching constitutions:", error);
        }
    };

    const fetchMemberLinkOptions = async () => {
        try {
            const [membersResponse, typesResponse, statusesResponse] = await Promise.all([
                axios.get("/api/resources/members?memberCategory=all"),
                axios.get("/api/membership-types"),
                axios.get("/api/membership-statuses"),
            ]);
            setMemberOptions((membersResponse.data || []).map((member) => ({
                value: member.id,
                label: [
                    [member.first_name, member.last_name].filter(Boolean).join(" "),
                    member.email_address,
                    member.current_lodge_names || member.current_lodge_name,
                ].filter(Boolean).join(" · "),
            })));
            setMembershipTypes(typesResponse.data || []);
            setMembershipStatuses(statusesResponse.data || []);
        } catch (error) {
            console.error("Error fetching lodge member link options:", error);
        }
    };

    // Convert UTC date to SAST date (YYYY-MM-DD)
    const toSAST = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const offsetInMs = 2 * 60 * 60 * 1000; // UTC+2
        const sastDate = new Date(date.getTime() + offsetInMs);
        return sastDate.toISOString().split("T")[0];
    };

    const formatDate = (dateString) => {
        const value = toSAST(dateString);
        return value || "Unknown";
    };

    const normalizeDuesSettings = (settings = {}) => ({
        ...emptyDuesSettings,
        ...settings,
        default_due_date: settings.default_due_date ? toSAST(settings.default_due_date) : "",
    });

    const normalizeDuesRate = (rate) => ({
        ...rate,
        due_date: rate.due_date ? toSAST(rate.due_date) : "",
    });

    const normalizeDuesMember = (member) => ({
        ...member,
        due_date: member.due_date ? toSAST(member.due_date) : "",
        paid_date: member.paid_date ? toSAST(member.paid_date) : "",
    });

    const selectedLodgeType = useMemo(
        () => lodgeTypes.find((t) => t.id === formData.lodge_type_id),
        [lodgeTypes, formData.lodge_type_id]
    );

    const selectedConstitution = useMemo(
        () => constitutions.find((c) => c.id === formData.constitution_id),
        [constitutions, formData.constitution_id]
    );

    const currentLocation = useMemo(() => {
        return locationHistory.find((loc) => !loc.end_date) || locationHistory[0] || null;
    }, [locationHistory]);

    const yearsActive = useMemo(() => {
        const start = Number(formData.year_established || (formData.date_established || "").slice(0, 4));
        const end = Number(formData.year_decommissioned || (formData.date_decommissioned || "").slice(0, 4)) || new Date().getFullYear();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return "Unknown";
        return `${end - start} years`;
    }, [formData.year_established, formData.year_decommissioned, formData.date_established, formData.date_decommissioned]);

    const isImageDocument = (doc) => {
        const value = `${doc.file_path || ""} ${doc.filename || ""} ${doc.file_type_description || ""}`;
        return /\.(png|jpe?g|gif|webp|bmp|tiff?)\b/i.test(value) || /image/i.test(value);
    };

    const openDocument = (doc) => {
        if (doc.file_path) {
            window.open(doc.file_path, "_blank", "noopener,noreferrer");
        }
    };

    const fetchLodgeContext = async (lodgeId) => {
        try {
            const res = await axios.get(`/api/resources/lodges/${lodgeId}/context`);
            setLodgeContext({
                documents: res.data.documents || [],
                meetings: res.data.meetings || [],
                members: res.data.members || [],
                counts: res.data.counts || { documents: 0, meetings: 0, members: 0 },
            });
        } catch (error) {
            console.error("Error fetching lodge context:", error);
            setLodgeContext({
                documents: [],
                meetings: [],
                members: [],
                counts: { documents: 0, meetings: 0, members: 0 },
            });
        }
    };

    const fetchLocationHistory = async (lodgeId) => {
        try {
            const res = await axios.get(`/api/resources/lodges/${lodgeId}/location-history`);
            const history = res.data.map((loc) => ({
                ...loc,
                start_date: toSAST(loc.start_date),
                end_date: loc.end_date ? toSAST(loc.end_date) : null,
            }));
            setLocationHistory(history);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.warn("No location history found for this lodge.");
                setLocationHistory([]);
            } else {
                console.error("Error fetching location history:", error);
            }
        }
    };

    // Handle lodge selection from the dropdown
    const handleLodgeSelect = async (id) => {
        if (!id) {
            setSelectedLodge(null);
            setFormData({ date_established: "", date_warrant_issued: "", date_decommissioned: "" }); // Clear dates
            setLocationHistory([]);
            setLodgeContext({ documents: [], meetings: [], members: [], counts: { documents: 0, meetings: 0, members: 0 } });
            setLodgeDues({ rates: [], members: [], availableYears: [] });
            setActiveTab("overview");
            setMemberLinkForm({ member_id: "", membership_type_id: "", membership_status_id: "", start_date: "" });
            return;
        }

        try {
            const res = await axios.get(`/api/resources/lodges/${id}`);
            setSelectedLodge(res.data);

            setFormData({
                ...res.data,
                date_established: res.data.date_established
                    ? toSAST(res.data.date_established)  // Ensure correct format
                    : "",
                date_warrant_issued: res.data.date_warrant_issued
                    ? toSAST(res.data.date_warrant_issued)
                    : "",
                date_decommissioned: res.data.date_decommissioned
                    ? toSAST(res.data.date_decommissioned)  // Ensure correct format
                    : "",
                warrant_document_id: (res.data.warrant_document_id || []).join(", "),
                general_document_id: (res.data.general_document_id || []).join(", "),
            });

            fetchLocationHistory(id);
            fetchLodgeContext(id);
            fetchLodgeDues(id, duesYear);
            setActiveTab("overview");
            setMemberLinkForm({ member_id: "", membership_type_id: "", membership_status_id: "", start_date: "" });
        } catch (error) {
            console.error("Error fetching lodge details:", error);
        }
    };

    const fetchLodgeDues = async (lodgeId = selectedLodge?.id, year = duesYear) => {
        if (!lodgeId) return;
        try {
            const res = await axios.get(`/api/resources/lodges/${lodgeId}/dues`, { params: { year } });
            setLodgeDues({
                rates: (res.data.rates || []).map(normalizeDuesRate),
                members: (res.data.members || []).map(normalizeDuesMember),
                availableYears: res.data.available_years || [],
            });
            setDuesSettingsForm(normalizeDuesSettings(res.data.settings || {}));
        } catch (error) {
            console.error("Error fetching lodge dues:", error);
            setLodgeDues({ rates: [], members: [], availableYears: [] });
            setDuesSettingsForm(emptyDuesSettings);
        }
    };

    const updateDuesYear = (value) => {
        const nextYear = Number(value) || new Date().getFullYear();
        setDuesYear(nextYear);
        if (selectedLodge?.id) fetchLodgeDues(selectedLodge.id, nextYear);
    };

    const updateDuesRateForm = (field, value) => {
        setDuesRateForm((current) => ({ ...current, [field]: value }));
    };

    const updateDuesSettingsForm = (field, value) => {
        setDuesSettingsForm((current) => ({ ...current, [field]: value }));
    };

    const saveDuesSettings = async () => {
        if (!selectedLodge?.id) return;
        try {
            const res = await axios.put(`/api/resources/lodges/${selectedLodge.id}/dues/settings`, {
                dues_year: duesYear,
                ...duesSettingsForm,
            });
            setDuesSettingsForm(normalizeDuesSettings(res.data || {}));
            alert("Payment guidance saved.");
        } catch (error) {
            console.error("Error saving lodge dues payment guidance:", error);
            alert(error.response?.data?.error || "Could not save payment guidance.");
        }
    };

    const saveDuesRate = async () => {
        if (!selectedLodge?.id) return;
        if (!duesRateForm.amount_due) {
            alert("Enter the annual amount due.");
            return;
        }
        try {
            await axios.post(`/api/resources/lodges/${selectedLodge.id}/dues/rates`, {
                dues_year: duesYear,
                ...duesRateForm,
                amount_due: Number(duesRateForm.amount_due) || 0,
                due_date: duesRateForm.due_date || duesSettingsForm.default_due_date || null,
            });
            setDuesRateForm({ membership_type_id: "", amount_due: "", due_date: "", notes: "" });
            await fetchLodgeDues(selectedLodge.id, duesYear);
        } catch (error) {
            console.error("Error saving lodge dues rate:", error);
            alert(error.response?.data?.error || "Could not save this dues rate.");
        }
    };

    const deriveDuesStatus = (member) => {
        if (!member.lodge_dues_rate_id) return "No rate";
        if (member.dues_status === "Waived") return "Waived";

        const amountDue = Number(member.amount_due || 0);
        const amountPaid = Number(member.amount_paid || 0);
        const dueDate = member.due_date ? new Date(member.due_date) : null;
        const isPastDue = dueDate && !Number.isNaN(dueDate.getTime()) && dueDate < new Date();

        if (amountDue <= 0) return "Pending";
        if (amountPaid >= amountDue) return "Paid";
        if (amountPaid > 0) return "Partial";
        if (isPastDue) return "Overdue";
        return "Pending";
    };

    const updateMemberDues = (memberId, field, value) => {
        setLodgeDues((current) => ({
            ...current,
            members: current.members.map((member) => {
                if (member.member_id !== memberId) return member;
                const updatedMember = { ...member, [field]: value };
                if (["amount_due", "amount_paid", "due_date"].includes(field)) {
                    updatedMember.dues_status = deriveDuesStatus(updatedMember);
                }
                return updatedMember;
            }),
        }));
    };

    const saveMemberDues = async (member) => {
        if (!selectedLodge?.id) return;
        if (!member.lodge_dues_rate_id) {
            alert("Create a matching dues rate for this member's category first.");
            return;
        }
        try {
            await axios.put(`/api/resources/lodges/${selectedLodge.id}/dues/members/${member.member_id}`, {
                lodge_dues_rate_id: member.lodge_dues_rate_id,
                membership_history_id: member.membership_history_id,
                amount_due: Number(member.amount_due) || 0,
                amount_paid: Number(member.amount_paid) || 0,
                status: member.dues_status || "Pending",
                due_date: member.due_date || null,
                paid_date: member.paid_date || null,
                notes: member.dues_notes || null,
            });
            await fetchLodgeDues(selectedLodge.id, duesYear);
        } catch (error) {
            console.error("Error saving member dues:", error);
            alert(error.response?.data?.error || "Could not save member dues.");
        }
    };

    const formatCurrency = (value) => {
        const amount = Number(value || 0);
        return amount.toLocaleString("en-ZA", {
            style: "currency",
            currency: "ZAR",
            maximumFractionDigits: 2,
        });
    };

    const getDuesStatusClass = (status) => `lodge-dues-status-${String(status || "Pending").toLowerCase().replace(/\s+/g, "-")}`;

    const isCurrentLodgeMember = useCallback((member) => {
        const statusName = String(member.membership_status || "").toLowerCase();
        const isActive = statusName === "active";
        const endDate = member.end_date ? new Date(member.end_date) : null;
        const hasEnded = endDate && !Number.isNaN(endDate.getTime()) && endDate < new Date();
        return isActive && !hasEnded;
    }, []);

    const filteredDuesMembers = useMemo(() => {
        if (duesMemberFilter === "current") {
            return lodgeDues.members.filter(isCurrentLodgeMember);
        }
        if (duesMemberFilter === "former") {
            return lodgeDues.members.filter((member) => !isCurrentLodgeMember(member));
        }
        return lodgeDues.members;
    }, [duesMemberFilter, isCurrentLodgeMember, lodgeDues.members]);

    const duesSummary = useMemo(() => ({
        expected: filteredDuesMembers.reduce((sum, member) => sum + Number(member.amount_due || 0), 0),
        collected: filteredDuesMembers.reduce((sum, member) => sum + Number(member.amount_paid || 0), 0),
        outstanding: filteredDuesMembers.reduce((sum, member) => sum + Math.max(Number(member.amount_due || 0) - Number(member.amount_paid || 0), 0), 0),
        paid: filteredDuesMembers.filter((member) => member.dues_status === "Paid").length,
    }), [filteredDuesMembers]);

    const handleMemberLinkChange = (field, value) => {
        setMemberLinkForm((current) => ({ ...current, [field]: value }));
    };

    const addMemberToLodge = async () => {
        if (!selectedLodge?.id || !memberLinkForm.member_id) {
            alert("Choose a member first.");
            return;
        }
        try {
            await axios.post(`/api/resources/members/${memberLinkForm.member_id}/history`, {
                lodge_id: selectedLodge.id,
                membership_type_id: memberLinkForm.membership_type_id || null,
                membership_status_id: memberLinkForm.membership_status_id || null,
                start_date: memberLinkForm.start_date || null,
                end_date: null,
                comments: "Linked from lodge workspace",
            });
            setMemberLinkForm({ member_id: "", membership_type_id: "", membership_status_id: "", start_date: "" });
            await fetchLodgeContext(selectedLodge.id);
            await fetchLodgeDues(selectedLodge.id, duesYear);
        } catch (error) {
            console.error("Error linking member to lodge:", error);
            alert(error.response?.data?.error || "Could not add this member to the lodge.");
        }
    };

    // Handle text inputs and textareas
    const handleInputChange = (e) => {
        const { name, value } = e.target || {};
        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Validate required fields
    const validateForm = () => {
        const requiredFields = [
            "code",
            "name",
            "year_established",
            "status",
            "lodge_type_id",
            "constitution_id",
        ];
        const newErrors = {};

        requiredFields.forEach((field) => {
            if (formData[field] === undefined || formData[field] === null || String(formData[field]).trim() === "") {
                newErrors[field] = `${field.replace(/_/g, " ")} is required.`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save or Update Lodge
    const handleSave = async () => {
        if (!validateForm()) {
            alert("Please fill in all required fields.");
            return;
        }

        try {
            const payload = {
                ...formData,
                warrant_document_id: formData.warrant_document_id
                    ? formData.warrant_document_id.split(",").map((id) => id.trim())
                    : [],
                general_document_id: formData.general_document_id
                    ? formData.general_document_id.split(",").map((id) => id.trim())
                    : [],
                date_established: formData.date_established || null, // Include date_established
                date_warrant_issued: formData.date_warrant_issued || null,
                date_decommissioned: formData.date_decommissioned || null, // Include date_decommissioned
            };

            if (selectedLodge) {
                await axios.put(`/api/resources/lodges/${selectedLodge.id}`, payload);
            } else {
                alert("No lodge selected.");
                return;
            }

            fetchLodges();
            alert("Lodge saved successfully!");
            if (onSave) onSave();
        } catch (error) {
            console.error("Error saving lodge:", error);
            alert("Error saving lodge. Please try again.");
        }
    };

    // Handle changes in the location form
    const handleLocationInputChange = (e) => {
        const { name, value } = e.target || {};
        setLocationForm({ ...locationForm, [name]: value });
    };

    // Save or Update a location record
    const handleSaveLocation = async () => {
        if (!selectedLodge) {
            alert("Please select a lodge first.");
            return;
        }
        try {
            const payload = {
                ...locationForm,
                start_date: locationForm.start_date
                    ? new Date(locationForm.start_date).toISOString()
                    : null,
                end_date: locationForm.end_date
                    ? new Date(locationForm.end_date).toISOString()
                    : null,
            };

            if (editingLocationId) {
                await axios.put(
                    `/api/resources/lodges/location-history/${editingLocationId}`,
                    payload
                );
            } else {
                await axios.post(
                    `/api/resources/lodges/${selectedLodge.id}/location-history`,
                    payload
                );
            }

            fetchLocationHistory(selectedLodge.id);
            fetchLodgeContext(selectedLodge.id);
            setLocationForm({});
            setEditingLocationId(null);
            alert("Location history saved successfully!");
        } catch (error) {
            console.error("Error saving location history:", error);
            alert("Failed to save location history.");
        }
    };

    // Edit an existing location
    const handleEditLocation = (loc) => {
        setEditingLocationId(loc.id);
        setLocationForm({
            ...loc,
            start_date: toSAST(loc.start_date),
            end_date: loc.end_date ? toSAST(loc.end_date) : "",
        });
    };

    // Delete a location record
    const handleDeleteLocation = async (id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) return;

        try {
            await axios.delete(`/api/resources/lodges/location-history/${id}`);
            fetchLocationHistory(selectedLodge.id);
            fetchLodgeContext(selectedLodge.id);
            alert("Location history deleted successfully!");
        } catch (error) {
            console.error("Error deleting location history:", error);
            alert("Failed to delete location history.");
        }
    };

    return (
        <div className="lodge-modal-overlay">
            <div className="lodge-modal-content">
                <button className="lodge-close-btn" onClick={onClose}>
                    &times;
                </button>

                <header className="lodge-workspace-header">
                    <div>
                        <p className="lodge-eyebrow">Lodge register</p>
                        <h2>{selectedLodge ? "Lodge Workspace" : "Select Lodge"}</h2>
                    </div>
                </header>

                <div className="lodge-form-group lodge-picker">
                    <label>Select Lodge</label>
                    <Select
                        className="top-level-lodge-select"
                        classNamePrefix="top-level-lodge-select"
                        options={lodges.map((lodge) => ({
                            value: lodge.id,
                            label: lodge.name,
                        }))}
                        onChange={(selectedOption) =>
                            handleLodgeSelect(selectedOption ? selectedOption.value : null)
                        }
                        value={
                            selectedLodge
                                ? { value: selectedLodge.id, label: selectedLodge.name }
                                : null
                        }
                        isSearchable
                        isClearable
                        placeholder="Choose a Lodge"
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                    />
                </div>

                {selectedLodge && (
                    <div className="lodge-workspace">
                        <aside className="lodge-profile-panel">
                            <h3>{formData.name}</h3>
                            <div className="lodge-profile-code">{formData.code}</div>
                            <div className="lodge-profile-chips">
                                {formData.status && <span>{formData.status}</span>}
                                {selectedConstitution && <span>{selectedConstitution.description}</span>}
                                {selectedLodgeType && <span>{selectedLodgeType.description}</span>}
                            </div>
                            <dl className="lodge-profile-list">
                                <dt>Warrant issued</dt>
                                <dd>{formatDate(formData.date_warrant_issued)}</dd>
                                <dt>Consecrated</dt>
                                <dd>{formatDate(formData.date_established) || formData.year_established || "Unknown"}</dd>
                                <dt>Surrendered</dt>
                                <dd>{formatDate(formData.date_decommissioned) || formData.year_decommissioned || "Present/unknown"}</dd>
                                <dt>Years active</dt>
                                <dd>{yearsActive}</dd>
                                <dt>Current temple</dt>
                                <dd>{currentLocation?.building_name || buildings.find((b) => b.id === currentLocation?.building_id)?.name || "Unknown"}</dd>
                            </dl>
                            <div className="lodge-kpi-grid">
                                <div><strong>{lodgeContext.counts.documents}</strong><span>Documents</span></div>
                                <div><strong>{lodgeContext.counts.meetings}</strong><span>Meetings</span></div>
                                <div><strong>{lodgeContext.counts.members}</strong><span>Members</span></div>
                                <div><strong>{locationHistory.length}</strong><span>Locations</span></div>
                            </div>
                        </aside>

                        <section className="lodge-editor-panel">
                            <nav className="lodge-tabs" aria-label="Lodge workspace tabs">
                                {[
                                    ["overview", "Overview"],
                                    ["locations", "Location History"],
                                    ["documents", "Documents"],
                                    ["meetings", "Meetings"],
                                    ["members", "Members"],
                                    ["dues", "Dues"],
                                    ["notes", "Notes"],
                                ].map(([tab, label]) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        className={activeTab === tab ? "active" : ""}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </nav>

                            {activeTab === "overview" && (
                                <div className="lodge-form-grid">
                                    <div className="lodge-form-group">
                                        <label>Lodge Code</label>
                                        <input type="text" name="code" value={formData.code || ""} onChange={handleInputChange} className={errors.code ? "error" : ""} />
                                        {errors.code && <p className="error-message">{errors.code}</p>}
                                    </div>
                                    <div className="lodge-form-group">
                                        <label>Lodge Name</label>
                                        <input type="text" name="name" value={formData.name || ""} onChange={handleInputChange} className={errors.name ? "error" : ""} />
                                        {errors.name && <p className="error-message">{errors.name}</p>}
                                    </div>
                                    <div className="lodge-form-group">
                                        <label>Lodge Type</label>
                                        <Select
                                            options={lodgeTypes.map((type) => ({ value: type.id, label: type.description }))}
                                            onChange={(selectedOption) =>
                                                handleInputChange({ target: { name: "lodge_type_id", value: selectedOption ? selectedOption.value : "" } })
                                            }
                                            value={selectedLodgeType ? { value: formData.lodge_type_id, label: selectedLodgeType.description } : null}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Lodge Type"
                                        />
                                        {errors.lodge_type_id && <p className="error-message">{errors.lodge_type_id}</p>}
                                    </div>
                                    <div className="lodge-form-group">
                                        <label>Constitution</label>
                                        <Select
                                            options={constitutions.map((c) => ({ value: c.id, label: c.description }))}
                                            onChange={(selectedOption) =>
                                                handleInputChange({ target: { name: "constitution_id", value: selectedOption ? selectedOption.value : "" } })
                                            }
                                            value={selectedConstitution ? { value: formData.constitution_id, label: selectedConstitution.description } : null}
                                            isSearchable
                                            isClearable
                                            placeholder="Select Constitution"
                                        />
                                        {errors.constitution_id && <p className="error-message">{errors.constitution_id}</p>}
                                    </div>
                                    {[
                                        ["year_established", "Year Consecrated"],
                                        ["date_warrant_issued", "Date Warrant Issued"],
                                        ["date_established", "Date Consecrated"],
                                        ["status", "Status"],
                                        ["year_decommissioned", "Year Surrendered"],
                                        ["date_decommissioned", "Date Surrendered"],
                                    ].map(([key, label]) => (
                                        <div className="lodge-form-group" key={key}>
                                            <label>{label}</label>
                                            <input
                                                type={key.includes("date_") ? "date" : "text"}
                                                name={key}
                                                value={formData[key] || ""}
                                                onChange={handleInputChange}
                                                className={errors[key] ? "error" : ""}
                                            />
                                            {errors[key] && <p className="error-message">{errors[key]}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {activeTab === "locations" && (
                                <div className="lodge-context-panel">
                                    <h3>Location History</h3>
                                    {locationHistory.length > 0 ? (
                                        <div className="lodge-timeline-list">
                                            {locationHistory.map((location) => {
                                                const building = buildings.find((b) => b.id === location.building_id);
                                                return (
                                                    <article key={location.id}>
                                                        <strong>{building?.name || location.building_name || "Unknown building"}</strong>
                                                        <span>{location.start_date || "Unknown"} - {location.end_date || "Present"}</span>
                                                        {location.comments && <p>{location.comments}</p>}
                                                        <div>
                                                            <button type="button" onClick={() => handleEditLocation(location)}>Edit</button>
                                                            <button type="button" className="danger" onClick={() => handleDeleteLocation(location.id)}>Delete</button>
                                                        </div>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="lodge-empty-note">No location history available.</p>
                                    )}

                                    <div className="lodge-location-editor">
                                        <h4>{editingLocationId ? "Edit Location" : "Add New Location"}</h4>
                                        <div className="lodge-form-grid">
                                            <div className="lodge-form-group">
                                                <label>Building</label>
                                                <Select
                                                    options={buildings.map((b) => ({ value: b.id, label: b.name }))}
                                                    onChange={(selected) => setLocationForm({ ...locationForm, building_id: selected ? selected.value : "" })}
                                                    value={
                                                        buildings.find((b) => b.id === locationForm.building_id)
                                                            ? { value: locationForm.building_id, label: buildings.find((b) => b.id === locationForm.building_id)?.name }
                                                            : null
                                                    }
                                                    isSearchable
                                                    isClearable
                                                    placeholder="Select Building"
                                                />
                                            </div>
                                            <div className="lodge-form-group">
                                                <label>Start Date</label>
                                                <input type="date" name="start_date" value={locationForm.start_date || ""} onChange={handleLocationInputChange} />
                                            </div>
                                            <div className="lodge-form-group">
                                                <label>End Date</label>
                                                <input type="date" name="end_date" value={locationForm.end_date || ""} onChange={handleLocationInputChange} />
                                            </div>
                                            <div className="lodge-form-group wide">
                                                <label>Comments</label>
                                                <textarea name="comments" value={locationForm.comments || ""} onChange={handleLocationInputChange} placeholder="Comments" />
                                            </div>
                                        </div>
                                        <button type="button" className="lodge-primary-action" onClick={handleSaveLocation}>
                                            {editingLocationId ? "Update Location" : "Add Location"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {activeTab === "documents" && (
                                <div className="lodge-context-panel">
                                    <div className="lodge-list-heading">
                                        <h3>All Linked Documents</h3>
                                        <span>{lodgeContext.counts.documents} total</span>
                                    </div>
                                    <div className="lodge-form-grid">
                                        <div className="lodge-form-group wide">
                                            <label>Warrant Document IDs</label>
                                            <input type="text" name="warrant_document_id" value={formData.warrant_document_id || ""} onChange={handleInputChange} />
                                        </div>
                                        <div className="lodge-form-group wide">
                                            <label>General Document IDs</label>
                                            <input type="text" name="general_document_id" value={formData.general_document_id || ""} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    {lodgeContext.documents.length === 0 ? (
                                        <p className="lodge-empty-note">No documents are linked to this lodge yet.</p>
                                    ) : (
                                        <div className="lodge-doc-list">
                                            {lodgeContext.documents.map((doc) => (
                                                <article key={doc.id}>
                                                    {isImageDocument(doc) && doc.file_path && (
                                                        <img
                                                            src={documentThumbnailUrl(doc.file_path)}
                                                            alt=""
                                                            onError={(event) => applyImageFallback(event, doc.file_path)}
                                                        />
                                                    )}
                                                    <div>
                                                        <strong>{doc.title || doc.filename || doc.id}</strong>
                                                        <span>{formatDate(doc.date_created)} · {doc.document_type_description || "Document"} · {doc.file_type_description || "File"}</span>
                                                        {doc.description && <p>{doc.description}</p>}
                                                        <button type="button" onClick={() => openDocument(doc)}>Open Document</button>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === "meetings" && (
                                <div className="lodge-context-panel">
                                    <div className="lodge-list-heading">
                                        <h3>All Linked Meetings</h3>
                                        <span>{lodgeContext.counts.meetings} total</span>
                                    </div>
                                    {lodgeContext.meetings.length === 0 ? (
                                        <p className="lodge-empty-note">No meetings are linked to this lodge yet.</p>
                                    ) : (
                                        <div className="lodge-simple-list">
                                            {lodgeContext.meetings.map((meeting) => (
                                                <article key={meeting.id}>
                                                    <strong>{formatDate(meeting.meeting_date)}</strong>
                                                    <span>{meeting.event_type || "Meeting"}</span>
                                                    {meeting.description && <p>{meeting.description}</p>}
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === "members" && (
                                <div className="lodge-context-panel">
                                    <div className="lodge-list-heading">
                                        <h3>All Linked Members</h3>
                                        <span>{lodgeContext.counts.members} total</span>
                                    </div>
                                    <div className="lodge-member-link-panel">
                                        <h4>Add member to this lodge</h4>
                                        <div className="lodge-member-link-grid">
                                            <label>
                                                Member
                                                <Select
                                                    value={memberOptions.find((option) => option.value === memberLinkForm.member_id) || null}
                                                    onChange={(option) => handleMemberLinkChange("member_id", option?.value || "")}
                                                    options={memberOptions}
                                                    placeholder="Search member"
                                                    isSearchable
                                                    isClearable
                                                />
                                            </label>
                                            <label>
                                                Membership type
                                                <Select
                                                    value={membershipTypes.map((type) => ({ value: type.id, label: type.name })).find((option) => option.value === memberLinkForm.membership_type_id) || null}
                                                    onChange={(option) => handleMemberLinkChange("membership_type_id", option?.value || "")}
                                                    options={membershipTypes.map((type) => ({ value: type.id, label: type.name }))}
                                                    placeholder="Optional type"
                                                    isSearchable
                                                    isClearable
                                                />
                                            </label>
                                            <label>
                                                Status
                                                <Select
                                                    value={membershipStatuses.map((status) => ({ value: status.id, label: status.name })).find((option) => option.value === memberLinkForm.membership_status_id) || null}
                                                    onChange={(option) => handleMemberLinkChange("membership_status_id", option?.value || "")}
                                                    options={membershipStatuses.map((status) => ({ value: status.id, label: status.name }))}
                                                    placeholder="Optional status"
                                                    isSearchable
                                                    isClearable
                                                />
                                            </label>
                                            <label>
                                                Joined / start date
                                                <input
                                                    type="date"
                                                    value={memberLinkForm.start_date || ""}
                                                    onChange={(event) => handleMemberLinkChange("start_date", event.target.value)}
                                                />
                                            </label>
                                            <button type="button" className="lodge-primary-action" onClick={addMemberToLodge}>
                                                Add to Lodge
                                            </button>
                                        </div>
                                    </div>
                                    {lodgeContext.members.length === 0 ? (
                                        <p className="lodge-empty-note">No membership history is linked to this lodge yet.</p>
                                    ) : (
                                        <div className="lodge-simple-list">
                                            {lodgeContext.members.map((member) => (
                                                <article key={member.id}>
                                                    <div className="lodge-member-row">
                                                        <div>
                                                            <strong>{[member.first_name, member.last_name].filter(Boolean).join(" ") || member.id}</strong>
                                                            <span>{member.status || "Status unknown"} · {member.membership_type || "Membership"}</span>
                                                            <p>{formatDate(member.start_date)} - {member.end_date ? formatDate(member.end_date) : "Present/unknown"}</p>
                                                            {(member.email_address || member.phone_number) && (
                                                                <p>{[member.email_address, member.phone_number].filter(Boolean).join(" · ")}</p>
                                                            )}
                                                        </div>
                                                        <button type="button" onClick={() => setEditingMemberId(member.id)}>Edit Member</button>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === "dues" && (
                                <div className="lodge-context-panel">
                                    <div className="lodge-list-heading">
                                        <div>
                                            <h3>Dues Management</h3>
                                            <p className="lodge-dues-period-note">
                                                {duesSettingsForm.period_label || `${duesYear} dues year`} · rates, payments, due date, and banking details are stored separately for this year.
                                            </p>
                                        </div>
                                        <div className="lodge-dues-toolbar">
                                            <label className="lodge-dues-filter">
                                                Members
                                                <select value={duesMemberFilter} onChange={(event) => setDuesMemberFilter(event.target.value)}>
                                                    <option value="current">Current members</option>
                                                    <option value="former">Not current</option>
                                                    <option value="all">All linked members</option>
                                                </select>
                                            </label>
                                            <label className="lodge-dues-year">
                                                Year
                                                <input
                                                    type="number"
                                                    min="1900"
                                                    max="2200"
                                                    value={duesYear}
                                                    onChange={(event) => updateDuesYear(event.target.value)}
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    {lodgeDues.availableYears.length > 0 && (
                                        <div className="lodge-dues-year-history">
                                            <span>Saved years</span>
                                            {lodgeDues.availableYears.map((year) => (
                                                <button
                                                    key={year}
                                                    type="button"
                                                    className={Number(year) === Number(duesYear) ? "active" : ""}
                                                    onClick={() => updateDuesYear(year)}
                                                >
                                                    {year}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="lodge-dues-summary">
                                        <div>
                                            <strong>{formatCurrency(duesSummary.expected)}</strong>
                                            <span>Expected</span>
                                        </div>
                                        <div>
                                            <strong>{formatCurrency(duesSummary.collected)}</strong>
                                            <span>Collected</span>
                                        </div>
                                        <div>
                                            <strong>{formatCurrency(duesSummary.outstanding)}</strong>
                                            <span>Outstanding</span>
                                        </div>
                                        <div>
                                            <strong>{duesSummary.paid}</strong>
                                            <span>Paid in full</span>
                                        </div>
                                    </div>

                                    <div className="lodge-dues-payment-panel">
                                        <div>
                                            <h4>Payment guidance</h4>
                                            <p>Set the lodge's payable date, banking details, and reference wording for this dues year.</p>
                                        </div>
                                        <div className="lodge-dues-payment-grid">
                                            <label>
                                                Period label
                                                <input
                                                    type="text"
                                                    value={duesSettingsForm.period_label || ""}
                                                    onChange={(event) => updateDuesSettingsForm("period_label", event.target.value)}
                                                    placeholder={`${duesYear} / ${Number(duesYear) + 1}`}
                                                />
                                            </label>
                                            <label>
                                                Payable date
                                                <input
                                                    type="date"
                                                    value={duesSettingsForm.default_due_date || ""}
                                                    onChange={(event) => updateDuesSettingsForm("default_due_date", event.target.value)}
                                                />
                                            </label>
                                            <label>
                                                Bank
                                                <input
                                                    type="text"
                                                    value={duesSettingsForm.bank_name || ""}
                                                    onChange={(event) => updateDuesSettingsForm("bank_name", event.target.value)}
                                                    placeholder="e.g. FNB"
                                                />
                                            </label>
                                            <label>
                                                Account name
                                                <input
                                                    type="text"
                                                    value={duesSettingsForm.account_name || ""}
                                                    onChange={(event) => updateDuesSettingsForm("account_name", event.target.value)}
                                                    placeholder="Lodge account name"
                                                />
                                            </label>
                                            <label>
                                                Account number
                                                <input
                                                    type="text"
                                                    value={duesSettingsForm.account_number || ""}
                                                    onChange={(event) => updateDuesSettingsForm("account_number", event.target.value)}
                                                    placeholder="Account number"
                                                />
                                            </label>
                                            <label>
                                                Branch code
                                                <input
                                                    type="text"
                                                    value={duesSettingsForm.branch_code || ""}
                                                    onChange={(event) => updateDuesSettingsForm("branch_code", event.target.value)}
                                                    placeholder="Branch code"
                                                />
                                            </label>
                                            <label className="wide">
                                                Required reference
                                                <input
                                                    type="text"
                                                    value={duesSettingsForm.reference_instructions || ""}
                                                    onChange={(event) => updateDuesSettingsForm("reference_instructions", event.target.value)}
                                                    placeholder="e.g. Surname + initials + dues year"
                                                />
                                            </label>
                                            <label className="wide">
                                                Payment notes
                                                <textarea
                                                    rows={2}
                                                    value={duesSettingsForm.payment_notes || ""}
                                                    onChange={(event) => updateDuesSettingsForm("payment_notes", event.target.value)}
                                                    placeholder="Optional extra instructions for the secretary or members"
                                                />
                                            </label>
                                        </div>
                                        <button type="button" className="lodge-primary-action" onClick={saveDuesSettings}>Save Payment Guide</button>
                                    </div>

                                    <div className="lodge-dues-rate-panel">
                                        <h4>Annual rate by membership category</h4>
                                        <p className="lodge-dues-rate-help">
                                            Rates are matched to members by membership category. The payable date above is used unless this row has its own due date.
                                        </p>
                                        <div className="lodge-dues-rate-grid">
                                            <label>
                                                Membership category
                                                <Select
                                                    value={membershipTypes.map((type) => ({ value: type.id, label: type.name })).find((option) => option.value === duesRateForm.membership_type_id) || null}
                                                    onChange={(option) => updateDuesRateForm("membership_type_id", option?.value || "")}
                                                    options={membershipTypes.map((type) => ({ value: type.id, label: type.name }))}
                                                    placeholder="All / default"
                                                    isSearchable
                                                    isClearable
                                                />
                                            </label>
                                            <label>
                                                Amount due
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={duesRateForm.amount_due}
                                                    onChange={(event) => updateDuesRateForm("amount_due", event.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </label>
                                            <label>
                                                Due date
                                                <input
                                                    type="date"
                                                    value={duesRateForm.due_date || duesSettingsForm.default_due_date || ""}
                                                    onChange={(event) => updateDuesRateForm("due_date", event.target.value)}
                                                />
                                            </label>
                                            <label>
                                                Notes
                                                <input
                                                    type="text"
                                                    value={duesRateForm.notes}
                                                    onChange={(event) => updateDuesRateForm("notes", event.target.value)}
                                                    placeholder="Optional"
                                                />
                                            </label>
                                            <button type="button" className="lodge-primary-action" onClick={saveDuesRate}>Save Rate</button>
                                        </div>
                                        {lodgeDues.rates.length > 0 && (
                                            <div className="lodge-dues-rate-list">
                                                {lodgeDues.rates.map((rate) => (
                                                    <span key={rate.id}>
                                                        {rate.membership_type_name || "Default"}: {formatCurrency(rate.amount_due)}
                                                        {rate.due_date ? ` due ${formatDate(rate.due_date)}` : ""}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {filteredDuesMembers.length === 0 ? (
                                        <p className="lodge-empty-note">No members match this dues filter.</p>
                                    ) : (
                                        <div className="lodge-dues-table">
                                            <div className="lodge-dues-row lodge-dues-heading">
                                                <span>Member</span>
                                                <span>Category</span>
                                                <span>Due</span>
                                                <span>Paid</span>
                                                <span>Status</span>
                                                <span>Balance</span>
                                                <span>Action</span>
                                            </div>
                                            {filteredDuesMembers.map((member) => {
                                                const balance = Math.max(Number(member.amount_due || 0) - Number(member.amount_paid || 0), 0);
                                                const statusClass = getDuesStatusClass(member.dues_status);
                                                return (
                                                    <div key={member.member_id} className={`lodge-dues-row ${statusClass}`}>
                                                        <span>
                                                            <strong>{[member.first_name, member.last_name].filter(Boolean).join(" ") || "Unnamed member"}</strong>
                                                            {member.email_address && <small>{member.email_address}</small>}
                                                        </span>
                                                        <span>
                                                            <strong>{member.membership_type || "No category"}</strong>
                                                            <small className={isCurrentLodgeMember(member) ? "lodge-dues-member-current" : "lodge-dues-member-former"}>
                                                                {isCurrentLodgeMember(member) ? "Current" : "Not current"}
                                                            </small>
                                                        </span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={member.amount_due ?? ""}
                                                            onChange={(event) => updateMemberDues(member.member_id, "amount_due", event.target.value)}
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={member.amount_paid ?? ""}
                                                            onChange={(event) => updateMemberDues(member.member_id, "amount_paid", event.target.value)}
                                                        />
                                                        <select
                                                            className={statusClass}
                                                            value={member.dues_status || "Pending"}
                                                            onChange={(event) => updateMemberDues(member.member_id, "dues_status", event.target.value)}
                                                        >
                                                            <option>Pending</option>
                                                            <option>Partial</option>
                                                            <option>Paid</option>
                                                            <option>Overdue</option>
                                                            <option>Waived</option>
                                                            <option>No rate</option>
                                                        </select>
                                                        <span className={balance > 0 ? "lodge-dues-balance-owing" : "lodge-dues-balance-clear"}>{formatCurrency(balance)}</span>
                                                        <button type="button" onClick={() => saveMemberDues(member)} disabled={!member.lodge_dues_rate_id}>
                                                            Save
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === "notes" && (
                                <div className="lodge-context-panel">
                                    <h3>Notes / History</h3>
                                    <div className="lodge-form-group">
                                        <label>Notes</label>
                                        <textarea name="notes" value={formData.notes || ""} onChange={handleInputChange} rows="8" />
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                )}

                {selectedLodge && (
                    <div className="lodge-modal-actions">
                        <button className="lodge-save-btn" onClick={handleSave}>Save Lodge</button>
                        <button className="lodge-cancel-btn" onClick={onClose}>Cancel</button>
                    </div>
                )}
            </div>
            {editingMemberId && (
                <MemberDetailsModal
                    initialMemberId={editingMemberId}
                    onClose={() => {
                        setEditingMemberId(null);
                        if (selectedLodge?.id) fetchLodgeContext(selectedLodge.id);
                    }}
                    onSave={() => {
                        if (selectedLodge?.id) fetchLodgeContext(selectedLodge.id);
                    }}
                />
            )}
        </div>
    );
}

export default LodgeDetailsModal;
